import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import { updateUserInformation } from './../../actions/AuthAction'
import * as translationService from './../../services/translationService'
import * as routeService from './../../services/routeService'
import * as networkService from './../../services/networkService'
import * as userService from './../../services/userService'
import { validateProfile } from './../../utils/validation'
import {
  Stack,
  TextField,
  MobileNumber,
  ButtonGroup,
  Button,
  DisplayText,
  TextContainer,
  ErrorText,
} from './../../components'
import { dump } from '../../services/debugService'

const AccountView = ({ user, updateUserInformation }) => {
  const [person, setPerson] = React.useState({})
  const [showResendVerificationMail, setShowResendVerificationMail] = React.useState(false)
  const [fieldErrors, setFieldErrors] = React.useState({})
  const [informationIsUpdated, setInformationIsUpdated] = React.useState(false)
  const [generalError, setGeneralError] = React.useState()

  React.useEffect(
    () => {
      const { firstname, lastname, phone, email, unverifiedEmail, unverifiedPhone } = user

      const person = {
        firstname,
        lastname,
        phone: unverifiedPhone || phone,
        email: unverifiedEmail || email,
      }

      if (person.email !== user.email) {
        setShowResendVerificationMail(true)
      }

      setPerson(person)
    },
    [user],
  )

  const hasUnverifiedEmail = person.email && person.email.replace(/\s+/g, '').toLowerCase() !== user.email
  const hasUnverifiedPhone = person.phone && person.phone.replace(/([^0-9|+|/])/g, '').toLowerCase() !== user.phone

  const onChange = ({ target: { name, value } }) => {
    setPerson({
      ...person,
      [name]: value,
    })
  }

  const handleUserDelete = async () => {
    const deleteUser = confirm(translationService.deleteAccountConfirm())
    if (deleteUser) {
      try {
        await networkService.removeApplications()
        await networkService.removeProfile()
        userService.logOutUser()
      } catch (err) {
        setGeneralError(translationService.deleteAccountFailed())
      }
    }
  }

  const savePersonInformation = async event => {
    event.preventDefault()

    setInformationIsUpdated(false)

    const personToSave = { firstname: person.firstname, lastname: person.lastname }

    const personEmail = person.email ? person.email.replace(/\s+/g, '').toLowerCase() : undefined
    const personPhone = person.phone ? person.phone.replace(/([^0-9|+|/])/g, '').toLowerCase() : undefined

    if (hasUnverifiedEmail || (user.unverifiedEmail && user.unverifiedEmail !== personEmail)) {
      personToSave.unverifiedEmail = personEmail
      setShowResendVerificationMail(true)
    }

    if (hasUnverifiedPhone || (user.unverifiedPhone && user.unverifiedPhone !== personPhone)) {
      personToSave.unverifiedPhone = personPhone
    }

    Object.keys(personToSave).forEach(key => personToSave[key] == null && delete personToSave[key])

    try {
      await validateProfile(personToSave)
      const { success, data: updatedPerson } = await updateUserInformation(personToSave)
      if (success) {
        const { unverifiedPhone, unverifiedEmail, unverifiedPhoneIsVerifiable } = updatedPerson
        if (unverifiedPhone && unverifiedPhoneIsVerifiable) {
          routeService.gotToPhoneNumberVerificationPage({ redirectBack: true })
        }

        if (unverifiedEmail && unverifiedPhone && !unverifiedPhoneIsVerifiable) {
          routeService.goToEmailVerificationPage({ redirectBack: true })
        }
        setInformationIsUpdated(true)
      }
    } catch (error) {
      console.log({ error })

      if (error.data) {
        const errorMap = error.data.details.messages
        setInformationIsUpdated(false)
        setFieldErrors({
          email: errorMap.unverifiedEmail || errorMap.email,
          phone: errorMap.unverifiedPhone || errorMap.phone,
        })
      } else {
        if (
          error.serverError &&
          error.serverError.response.data.error.details &&
          error.serverError.response.data.error.details.codes
        ) {
          Object.keys(error.serverError.response.data.error.details.codes).map(key => {
            error.serverError.response.data.error.details.codes[
              key
            ] = error.serverError.response.data.error.details.codes[key]
              .map(code => translationService.formError(`${key}.${code}`))
              .join(', ')
          })
          dump({ errors: error.serverError.response.data.error.details.codes })
          setFieldErrors(error.serverError.response.data.error.details.codes)
        } else {
          setFieldErrors(error)
        }
        setShowResendVerificationMail(false)
      }
    }
  }

  const resendVerification = async () => {
    try {
      await networkService.resendVerificationMail(person.email)
      setShowResendVerificationMail(false)
    } catch (error) {
      if (error.data) {
        const errorMap = error.data.details.messages
        setInformationIsUpdated(false)
        setFieldErrors({
          email: errorMap.unverifiedEmail || errorMap.email,
          phone: errorMap.unverifiedPhone || errorMap.phone,
        })
      } else {
        setFieldErrors(error)
      }
    }
  }

  const getEmailHelpText = () => {
    if (hasUnverifiedEmail && showResendVerificationMail) {
      return (
        <React.Fragment>
          {translationService.profileHistoryInfoEmailHelpText()}{' '}
          <Button onClick={resendVerification} plain>
            {translationService.profileResendVerificationMail()}
          </Button>
        </React.Fragment>
      )
    }
  }

  return (
    <form onSubmit={savePersonInformation}>
      <TextContainer>
        {informationIsUpdated && (
          <DisplayText size="extraSmall">{translationService.t('overview.profile.information.updated')}</DisplayText>
        )}
        {generalError && <ErrorText>{generalError}</ErrorText>}
        <Stack distribution="fill">
          <TextField
            label={translationService.t('system.firstname')}
            id="firstname"
            name="firstname"
            value={person.firstname}
            onChange={onChange}
            error={fieldErrors && fieldErrors.firstname}
          />
          <TextField
            label={translationService.t('system.lastname')}
            id="lastname"
            name="lastname"
            value={person.lastname}
            onChange={onChange}
            error={fieldErrors && fieldErrors.lastname}
          />
        </Stack>
        <TextField
          label={
            translationService.t('system.email') +
            (hasUnverifiedEmail ? ` ${translationService.profileHistoryInfoUnverifiedText()}` : '')
          }
          id="email"
          name="email"
          helpText={getEmailHelpText()}
          value={person.email}
          onChange={onChange}
          error={fieldErrors && fieldErrors.email}
        />
        <MobileNumber
          label={
            translationService.t('system.mobile_number') +
            (hasUnverifiedPhone ? ` ${translationService.profileHistoryInfoUnverifiedText()}` : '')
          }
          id="phone"
          name="phone"
          value={person.phone}
          onChange={onChange}
          error={fieldErrors && fieldErrors.phone}
        />
        <ButtonGroup>
          <Button submit primary>
            {translationService.buttonSave()}
          </Button>
          <Button plain error onClick={handleUserDelete}>
            {translationService.deleteAccountButton()}
          </Button>
        </ButtonGroup>
      </TextContainer>
    </form>
  )
}

AccountView.propTypes = {
  user: PropTypes.object,
  updateUserInformation: PropTypes.func,
}

const mapStateToProps = ({ AuthReducer }) => {
  const { user } = AuthReducer
  return {
    user: user || {},
  }
}
const mapDispatchToProps = dispatch => ({
  updateUserInformation: userInformation => dispatch(updateUserInformation(userInformation)),
})
export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(AccountView)



// WEBPACK FOOTER //
// ./src/views/Profile/Account.js